import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiLayers, FiPlay, FiSearch, FiRefreshCw, FiMusic, FiAlertCircle, FiLock } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import Tutorial from './Tutorial';
import { useSidebar } from '../contexts/SidebarContext';
import { useTutorial } from '../contexts/TutorialContext';
import { publicStructuralSegmentsAPI, StructuralSegment, authAPI, subscriptionAPI } from '../services/api';
import { canUseSegment, hasFullLibraryAccess } from '../utils/materialAccess';
import MaterialPagination from './MaterialPagination';
import './StructuredSegmentsList.css';

interface StructuredSegmentsListProps {
  onLogout?: () => void;
  userEmail?: string;
}

const StructuredSegmentsList: React.FC<StructuredSegmentsListProps> = ({ onLogout, userEmail }) => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { showTutorial, currentTutorialStep, completeTutorial, skipTutorial } = useTutorial();
  const [segments, setSegments] = useState<StructuralSegment[]>([]);
  const [completionMap, setCompletionMap] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
    is_staff: false
  });

  useEffect(() => {
    fetchUserProfile();
    checkSubscription();
  }, [userEmail]);

  useEffect(() => {
    if (hasSubscription !== null) {
      fetchSegments();
    }
  }, [hasSubscription, userInfo.is_staff, page, searchTerm]);

  const fetchUserProfile = async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || 'anonymous.user@naati.edu',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
        is_staff: profile.is_staff || false
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const status = await subscriptionAPI.getSubscriptionStatus();
      const profile = await authAPI.getProfile();
      const isStaff = profile.is_staff || false;
      setHasSubscription(status.has_subscription || isStaff);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasSubscription(false);
    }
  };

  const fetchSegments = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await publicStructuralSegmentsAPI.getActiveSegments({
        page,
        page_size: 24,
        q: searchTerm.trim() || undefined,
      });
      setSegments(res.results);
      setTotalPages(res.total_pages);
      setTotalCount(res.count);

      const full = hasFullLibraryAccess(hasSubscription, userInfo.is_staff);
      const unlockedIds = res.results
        .filter((seg) => full || seg.is_free_public)
        .map((seg) => seg.id);

      const completion: Record<number, boolean> = {};
      if (unlockedIds.length > 0) {
        const batch = await publicStructuralSegmentsAPI.batchCheckCompletion(unlockedIds);
        for (const [id, done] of Object.entries(batch)) {
          completion[Number(id)] = done;
        }
      }
      for (const seg of res.results) {
        if (!(seg.id in completion)) completion[seg.id] = false;
      }
      setCompletionMap(completion);
    } catch (err: any) {
      console.error('Error fetching segments:', err);
      setError(err.message || 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSegment = (segment: StructuralSegment) => {
    if (!canUseSegment(hasSubscription, userInfo.is_staff, segment.is_free_public)) {
      window.alert(
        'This segment requires an active subscription. Contact your administrator, or use segments marked as free.'
      );
      return;
    }
    navigate(`/material/structured-segments/${segment.id}`, {
      state: { segment, segmentId: segment.id }
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        
        <div className="dashboard-content">
          <div className="segments-list-page">
            <motion.div
              className="page-header"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="page-title">Structural Segments</h1>
              <p className="page-description">Listen to each segment, then review model answers and grammar notes</p>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              className="search-container"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="search-box">
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                <input
                  type="text"
                  placeholder="Search segments by name, category, or description..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1);
                    setSearchTerm(e.target.value);
                  }}
                />
              </div>
            </motion.div>

            {/* Library access notice (free items still available without subscription) */}
            {hasSubscription === false && !userInfo.is_staff && (
              <motion.div
                className="subscription-info-banner"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 20 })}
                <div>
                  <h3>Limited library</h3>
                  <p>
                    Without a subscription you can still open items marked <strong>Free</strong>. Subscribe for
                    full access to all structural segments.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                className="error-message"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {error}
                <button onClick={fetchSegments} className="retry-btn">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  <span> Retry</span>
                </button>
              </motion.div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="loading-container">
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 32 })}
                <p>Loading segments...</p>
              </div>
            ) : segments.length === 0 ? (
              <div className="empty-state">
                {React.createElement(FiLayers as React.ComponentType<{ size?: number }>, { size: 48 })}
                <p>No segments found</p>
                {searchTerm && (
                  <p className="empty-state-hint">Try a different search term</p>
                )}
              </div>
            ) : (
              <>
              <motion.div
                className="segments-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {segments.map((segment) => {
                  const isCompleted = completionMap[segment.id] ?? false;
                  const unlocked = canUseSegment(hasSubscription, userInfo.is_staff, segment.is_free_public);
                  return (
                  <motion.div
                    key={segment.id}
                    className={`segment-card ${isCompleted ? 'segment-completed' : ''} ${!unlocked ? 'segment-card-locked' : ''}`}
                    variants={itemVariants}
                    whileHover={unlocked ? { y: -5, boxShadow: '0 8px 16px rgba(0,0,0,0.2)' } : {}}
                    onClick={() => handleSelectSegment(segment)}
                  >
                    <div className="segment-card-header">
                      <div className="segment-icon">
                        {React.createElement(FiMusic as React.ComponentType<{ size?: number }>, { size: 24 })}
                      </div>
                      <div className="segment-info">
                        <h3 className="segment-name">{segment.name}</h3>
                        {segment.segment_number && (
                          <span className="segment-number">Segment {segment.segment_number}</span>
                        )}
                        {isCompleted && (
                          <span className="segment-completed-badge">Practiced</span>
                        )}
                        {segment.is_free_public && (
                          <span className="segment-free-badge">Free</span>
                        )}
                        {!unlocked && (
                          <span className="segment-lock-badge" title="Subscription required">
                            {React.createElement(FiLock as React.ComponentType<{ size?: number }>, { size: 12 })}
                          </span>
                        )}
                      </div>
                    </div>

                    {segment.description && (
                      <p className="segment-description">{segment.description}</p>
                    )}

                    <div className="segment-meta">
                      {segment.test_category && (
                        <span className="segment-category">{segment.test_category}</span>
                      )}
                      <span className="segment-audio-count">
                        {segment.audio_count} {segment.audio_count === 1 ? 'audio' : 'audios'}
                      </span>
                    </div>

                    <button
                      className="segment-play-btn"
                      title={isCompleted ? 'Practice this segment again' : 'Start practice'}
                    >
                      {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                      <span> {isCompleted ? 'Practice Again' : 'Start Practice'}</span>
                    </button>
                  </motion.div>
                );
                })}
              </motion.div>
              <MaterialPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setPage}
                disabled={loading}
              />
              </>
            )}
          </div>
        </div>
      </div>
      
      {showTutorial && currentTutorialStep === 3 && (
        <Tutorial
          onComplete={completeTutorial}
          onSkip={skipTutorial}
          initialStep={3}
        />
      )}
    </div>
  );
};

export default StructuredSegmentsList;

